import {
  Action,
  ActionMenu,
  IconButton,
  Pane,
  Spinner,
  SpinnerWithFailed,
  Table,
  TableBodyRow,
  TableBodyRowProps,
  TableColumnType,
  Text,
  useSorting,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { propertiesFailedToLoadRetry } from '@fullstory/library-ui';
import { EventFilter } from '@fullstory/proto/fs/api/segment';
import * as vars from '@fullstory/proto/fs/api/vars';
import { GetCustomSchemaConnectionsByPropertyRequest } from '@fullstory/proto/fs/services/fieldmeta';
import { useFlags } from '@fullstory/react-hooks';
import {
  AppDispatch,
  fullstorySelectors,
  SearchMetaAsyncRequestActions,
  SpecObjectInfo,
} from '@fullstory/redux';
import { FieldScope, parseUnscopedFieldName } from '@fullstory/search-vars';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MODAL_TABLE_MAX_HEIGHT } from '../../data-mgmt/constants';
import { usePropertiesTableColumns } from '../../data-mgmt/hooks/usePropertiesTableColumns';
import { usePropertyActions } from '../../data-mgmt/hooks/usePropertyActions';
import { usePropertySampleValues } from '../../data-mgmt/hooks/usePropertySampleValues';
import { PropertyRow } from '../../data-mgmt/types';
import { SampleValues } from '../SampleValues';

const { getOrgId } = fullstorySelectors;

type DetailModalPropertiesTableProps = {
  properties: PropertyRow[];
  connectionParent?: string | number;
  loading?: boolean;
  failed?: boolean;
  retryFailed?: () => void;
  canArchive: boolean;
  sampleValuesProps?: {
    customFilter?: EventFilter;
  };
  existingSpecObjects: SpecObjectInfo[];
};

const PropertyActionMenu = ({
  property,
  connectionParent,
  canArchive,
}: {
  property: PropertyRow;
  connectionParent?: string | number;
  canArchive: boolean;
}) => {
  const { archiveProperties } = usePropertyActions();
  const archived =
    property.archived || (connectionParent && property.archivedConnections.has(connectionParent));
  const selfArchived =
    property.selfArchived ||
    (connectionParent && property.selfArchivedConnections.has(connectionParent));
  const pending = property.pending;

  // NB: there will currently only ever be one action - archive or unarchive.  For consistency with bulk action
  // tables, this ActionMenu supports multiple actions.  A single action is rendered as an IconButton, while
  // multiple aCtions are rendered in an ActionMenu.
  const actions: Action[] = useMemo(() => {
    const ret: Action[] = [];
    // Only show unarchive action if the row is considered archived and the row identifier itself is hidden.
    // Unarchiving rows that are archived because their parent is hidden does not do anything.
    if (!pending) {
      if (archived && selfArchived) {
        ret.push({
          id: 'unarchive',
          iconName: 'folder',
          label: intl.formatMessage({ defaultMessage: 'Unarchive', id: 'ftDYZo' }),
          disabled: !canArchive,
        });
      } else if (!archived) {
        ret.push({
          id: 'archive',
          iconName: 'folder',
          label: intl.formatMessage({ defaultMessage: 'Archive', id: 'hrgo+E' }),
          disabled: !canArchive,
        });
      }
    }
    return ret;
  }, [pending, canArchive, archived, selfArchived]);

  if (actions.length === 0) {
    return null;
  } else if (actions.length === 1 && actions[0]?.iconName) {
    return (
      <IconButton
        title={actions[0]?.label}
        iconName={actions[0]?.iconName}
        onClick={e => {
          archiveProperties([property], !archived, connectionParent);
          e.stopPropagation();
        }}
        disabled={actions[0]?.disabled}
      />
    );
  } else {
    return (
      <ActionMenu
        onSelect={() => archiveProperties([property], !archived, connectionParent)}
        actions={actions}
      />
    );
  }
};

function PropertyRowComponent(props: TableBodyRowProps<PropertyRow>) {
  const { cellProps } = props;
  const modifiedCellProps = { ...cellProps, borders: 'bottom' };

  return (
    <TableBodyRow data-component="PropertyRowComponent" {...props} cellProps={modifiedCellProps} />
  );
}

export const DetailModalPropertiesTable = ({
  properties,
  connectionParent,
  loading,
  failed,
  retryFailed,
  canArchive,
  sampleValuesProps,
  existingSpecObjects,
}: DetailModalPropertiesTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();

  const showPropertyRole =
    flags.featureFlags().AppSpec1ToNAndProperties && existingSpecObjects.length > 0;

  const [fetched, setFetched] = React.useState(new Set<string>());
  const { sampleValues, fetchSampleValues } = usePropertySampleValues(sampleValuesProps ?? {});

  const propertiesToFetch = useMemo(() => {
    const propertiesToFetch: Parameters<typeof fetchSampleValues>[0] = [];
    properties.forEach(row => {
      // top level concrete types can be fetched directly (they can't be expanded)
      propertiesToFetch.push({
        id: row.id,
        name: row.displayName,
        propertyType: vars.Type[row.dataType],
        propertyScope:
          row.propertyType === '' ? vars.Scope.INVALID_SCOPE : vars.Scope[row.propertyType],
      });
    });
    return propertiesToFetch;
  }, [properties]);

  const loadRelatedData = useCallback(() => {
    const reqProperties = new Map<
      FieldScope,
      GetCustomSchemaConnectionsByPropertyRequest.Property[]
    >([
      [FieldScope.Event, []],
      [FieldScope.Page, []],
      [FieldScope.Element, []],
    ]);
    if (reqProperties.size) {
      propertiesToFetch
        .filter(prop => reqProperties.has(vars.Scope[prop.propertyScope] as FieldScope))
        .forEach(prop => {
          const fieldScope = vars.Scope[prop.propertyScope] as FieldScope;
          const nameTypeKey = `${prop.name}_${vars.Type[prop.propertyType]}`;
          const field = parseUnscopedFieldName(nameTypeKey, fieldScope);
          reqProperties.get(fieldScope)?.push({
            name: field.ident,
            type: prop.propertyType,
          });
        });
      reqProperties.forEach((properties, scope) => {
        if (scope == FieldScope.NoScope) {
          return; // need to do this for scope enum conversion below to keep the type checker happy
        }
        dispatch(
          SearchMetaAsyncRequestActions.getCustomSchemaConnectionsByProperty(
            orgId,
            properties,
            vars.Scope[scope],
          ),
        );
      });
    }
    // additionally, fetch sample values
    setFetched(new Set(fetchSampleValues(propertiesToFetch)));
  }, [propertiesToFetch, dispatch, orgId, fetchSampleValues]);

  useEffect(() => {
    loadRelatedData();
  }, [loadRelatedData]);

  const { displayName, dataType, connections, role } = usePropertiesTableColumns(
    loadRelatedData,
    connectionParent,
    existingSpecObjects,
    true,
  );
  const columns = React.useMemo(
    (): TableColumnType<PropertyRow>[] => [
      displayName,
      {
        ...dataType,
        width: '15%',
      },
      {
        key: 'sampleValues',
        label: intl.formatMessage({ defaultMessage: 'Sample Values', id: 'VaNVEE' }),
        renderCell(property) {
          if (!fetched.has(property.id)) {
            return <Text appearance="bodySmall">--</Text>;
          }
          const values = sampleValues.get(property.id);
          if (!values) {
            return <Spinner />;
          }
          return (
            <SampleValues
              sampleValues={values}
              justifyContent="flex-start"
              emptyMessage={
                property.pending
                  ? intl.formatMessage({ defaultMessage: 'Waiting to receive data', id: '8gVH3D' })
                  : undefined
              }
            />
          );
        },
      },
      {
        ...connections,
        width: '15%',
      },
      ...(showPropertyRole ? [role] : []),
      {
        key: 'actions',
        label: '',
        renderCell(property) {
          return (
            <PropertyActionMenu
              property={property}
              connectionParent={connectionParent}
              canArchive={canArchive}
            />
          );
        },
        width: 60,
      },
    ],
    [connectionParent, canArchive, dataType, displayName, sampleValues, connections, role, fetched],
  );

  const { rows, ...sorting } = useSorting(properties, 'displayName', {
    defaultSortDirection: 'asc',
  });

  return (
    <Pane
      data-component="DetailModalPropertiesTable"
      border="standard"
      maxHeight={MODAL_TABLE_MAX_HEIGHT}
      overflow="auto"
      borderRadius={4}
      padding={loading || failed ? 24 : 0}
    >
      {loading || failed ? (
        retryFailed ? (
          <SpinnerWithFailed
            failed={!!failed}
            onFailedRetry={retryFailed}
            failedMessage={propertiesFailedToLoadRetry}
          />
        ) : (
          <Spinner />
        )
      ) : (
        <Table
          rows={rows}
          columns={columns}
          rowKeyAccessor="id"
          borders="none"
          headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
          bodyProps={{ rowComponent: PropertyRowComponent }}
          fixed
          {...sorting}
        />
      )}
    </Pane>
  );
};
